/** @format */
'use client';
import updateLegacySettings from '@common/application/helpers/updateLegacySettings';
import { TSettings } from '@common/application/hooks/useDefaultSettings';
import useQueryFeedback from '@common/application/hooks/useQueryFeedback/useQueryFeedback';
import { User } from '@common/model/apiClient/User';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import { useSession } from 'next-auth/react';
import { useEffect, useReducer, useState } from 'react';

export enum SettingsKey {
  TABLES = 'tableSettings',
  KEY = 'keyMap',
  DOCUMENT = 'document',
}

const reducer = (state, newState) => {
  return { ...state, ...newState };
};

function useUserSettings(key: SettingsKey, defaultValues: TSettings) {
  const [settings, setSettings] = useReducer(reducer, defaultValues);
  const [isLoaded, setIsLoaded] = useState(false);
  const [savedSettings, setSavedSettings] = useState(JSON.stringify(defaultValues));
  const { notifySuccess, notifyFail } = useQueryFeedback();
  const { data: session } = useSession();

  const queryClient = useQueryClient();

  const updateSettings = useMutation({
    mutationKey: ['update_user_settings'],
    mutationFn: async (settings: {}) => await User.updateSettings(session?.user?.userId!, { [`trackUpSettings_${key}`]: JSON.stringify(settings) }),
    onSuccess: (_) => {
      setSavedSettings(JSON.stringify(settings));

      queryClient.invalidateQueries({ queryKey: [`trackUpSettings_${key}`] });
    },
    onError: (e: AxiosError<{ payload: any }>) => {
      notifyFail(e);
      throw new Error(e.message);
    },
  });

  const saveChanges = async (successCallback?: () => void) => {
    if (isEqual(JSON.parse(savedSettings), settings) || !updateSettings.isIdle) {
      return null;
    }
    return await updateSettings.mutateAsync(settings, { onSuccess: () => (successCallback ? successCallback() : notifySuccess()) });
  };

  const undoChanges = () => {
    setSettings(JSON.parse(savedSettings));
  };

  const onFetchUserSettings = (d: typeof userSettings.data) => {
    const payload = d?.data?.payload as
      | {
          representation?: {
            attributes?: Record<string, string>;
          };
        }
      | undefined;
    const settings = payload?.representation?.attributes?.[`trackUpSettings_${key}`];
    if (settings) {
      // console.log(settings);
      const parsed = JSON.parse(settings);
      // Ensure that settings are in correct format
      const atts = updateLegacySettings(key, parsed, defaultValues);
      const newSettings = {};
      Object.keys(defaultValues)?.forEach?.((key) => {
        if (typeof defaultValues[key] === 'string') {
          newSettings[key] = atts[key] ?? defaultValues[key];
        } else {
          newSettings[key] = { ...defaultValues[key], ...atts[key] };
        }
      });
      setSettings(newSettings);
      setSavedSettings(JSON.stringify(newSettings));
    }
    setIsLoaded(true);
  };

  const userSettings = useQuery({
    queryKey: [`trackUpSettings_${key}`],
    queryFn: () => User.get(session?.user?.userId!),
    enabled: !!session?.user?.userId,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  useEffect(() => {
    if (userSettings.data) {
      onFetchUserSettings(userSettings.data);
    }
  }, [userSettings.data]);

  useEffect(() => {
    if (userSettings.error) {
      setIsLoaded(true);
      //   Todo handle failure to load user data
      // notify({
      //   message: <FormattedMessage defaultMessage="Failed to fetch user data" description="User data error message" />,
      // });
    }
  }, [userSettings.error]);

  useEffect(() => {
    if (userSettings.isFetching) {
      setIsLoaded(false);
    }
  }, [userSettings.isFetching]);

  return { settings, setSettings, saveChanges, undoChanges, isLoaded };
}

export default useUserSettings;
